import { Button, ModalsContext, Loader } from '@ws/shared/components';
import {
  DownloadCloud20,
  FolderOpen24,
  Download20,
  Trash20,
  CloudArrows20,
} from '@ws/shared/icons';
import { TMixedProject, TProject, TShortProject } from '@ws/shared/types';
import { ComponentType, FC, useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { iDBActService } from '../../../resources/iDBAct';
import { iDBProjectService } from '../../../resources/iDBProject';
import { loadLayout } from '../../../utils/editorLayout/editorLayout.storage';
import { packToFile } from '../../../utils/packToFile';
import { EntityCard } from '../../EntityCard';

import styles from './ProjectCard.module.css';

interface IProjectEntityCardProps {
  project: TMixedProject;

  onDelete(id: string): void;

  onRemoteClick(id: string): void;
}

export const ProjectCard = projectsExistChecker(
  ({ project, isWithRemote, onDelete }) => {
    const navigate = useNavigate();
    const { id } = project;
    const { setModal } = useContext(ModalsContext);
    const handleDelete = useCallback(() => {
      setModal({
        title: 'Удаление проекта',
        subTitle: `Вы действительно хотите удалить "${project.meta.name}"?`,
        closeAfterAction: true,
        actions: [
          { text: 'Оставить', color: 'tertiary' },
          { text: 'Удалить', color: 'error', onClick: () => onDelete(id) },
        ],
      });
    }, [id, onDelete, project, setModal]);
    const handleDownloadProject = useCallback(async () => {
      const [wantedProject, wantedSyncState] = await Promise.all([
        iDBProjectService.getOneById(id),
        iDBActService.getOneById(id),
      ]);
      if (wantedProject && wantedSyncState) {
        packToFile(wantedProject, wantedSyncState);
      } else {
        toast.error(`Can't pack a file for this project`);
      }
    }, [id]);

    const beforeActions = useMemo(
      () => [
        <span
          key="Sync"
          className={styles['sync-status']}
          role="img"
          aria-label={isWithRemote ? 'Синхронизирован' : 'Только локально'}
          title={isWithRemote ? 'Синхронизирован' : 'Только локально'}
        >
          <CloudArrows20 color={isWithRemote ? 'other_success' : 'other_grey'} />
        </span>,
      ],
      [isWithRemote],
    );
    const afterActions = useMemo(
      () => [
        <Button
          key="download"
          variant="shapeless"
          icon={<Download20 />}
          onClick={handleDownloadProject}
        />,
        <Button
          key="delete"
          variant="shapeless"
          icon={<Trash20 color="other_red" />}
          onClick={handleDelete}
        />,
      ],
      [handleDownloadProject, handleDelete],
    );

    // Входим в проект на той заметке, на которой ушли.
    //
    // Здесь у нас полный TProject (projectsExistChecker отдаёт в эту ветку
    // project.local), поэтому сохранённую ссылку можно проверить до навигации и не
    // гонять пользователя через промежуточный редирект. Проверяем собственные
    // свойства: notes['__proto__'] вернул бы Object.prototype за живой узел.
    const setGoToPath = useCallback(() => {
      const savedNodeId = loadLayout(id)?.selected?.id;
      const isAlive =
        Boolean(savedNodeId) &&
        (Object.prototype.hasOwnProperty.call(project.notes, savedNodeId as string) ||
          Object.prototype.hasOwnProperty.call(project.collections, savedNodeId as string));

      // encodeURIComponent, потому что id приходит из localStorage: пользователь мог
      // положить туда что угодно, а слэш в сегменте развалил бы маршрут.
      const nodeId = isAlive ? (savedNodeId as string) : project.root;

      return `/project/${encodeURIComponent(id)}/node/${encodeURIComponent(nodeId)}`;
    }, [project.root, project.notes, project.collections, id]);

    const handleTitleClick = () => {
      navigate(setGoToPath());
    };

    return (
      <EntityCard
        title={project.meta.name}
        titleIcon={<FolderOpen24 />}
        beforeActions={beforeActions}
        afterActions={afterActions}
        onTitleClick={handleTitleClick}
      />
    );
  },
  ({ project, onRemoteClick }) => {
    const [isLoading, setIsLoading] = useState(false);

    const beforeActions = useMemo(() => {
      if (!isLoading) {
        return [
          <span
            key="Sync"
            className={styles['sync-status']}
            role="img"
            aria-label="Только в облаке"
            title="Только в облаке"
          >
            <DownloadCloud20 color="textator_primary" />
          </span>,
        ];
      }

      return [<Loader style={{ width: 20, height: 20 }} key="loader" />];
    }, [isLoading]);

    return (
      <EntityCard
        title={project.meta.name}
        titleIcon={<FolderOpen24 />}
        beforeActions={beforeActions}
        onTitleClick={() => {
          setIsLoading(true);
          onRemoteClick(project.id);
        }}
      />
    );
  },
);

interface ILocalProjectProps extends Omit<IProjectEntityCardProps, 'project' | 'onMakeLocal'> {
  project: TProject;
  isWithRemote: boolean;
}

interface IRemoteProjectProps extends Omit<IProjectEntityCardProps, 'project' | 'onDelete'> {
  project: TShortProject;
}

function projectsExistChecker(
  LocalCard: ComponentType<ILocalProjectProps>,
  RemoteCard: ComponentType<IRemoteProjectProps>,
): FC<IProjectEntityCardProps> {
  return function EntityExistChecker({ project, ...rest }) {
    if (project.local) {
      return <LocalCard {...rest} isWithRemote={Boolean(project.remote)} project={project.local} />;
    }

    if (project.remote) {
      return <RemoteCard {...rest} project={project.remote} />;
    }

    return null;
  };
}
